import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faBell,
  faCheck,
  faCheckDouble,
  faTimes,
  faSpinner,
  faExternalLinkAlt,
  faBellSlash
} from '@fortawesome/free-solid-svg-icons';
import notificationService from '../../services/notificationService';
import { useTheme } from '../../contexts/ThemeContext';

const NotificationBell = () => {
  const { darkMode } = useTheme();
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [notificationPermission, setNotificationPermission] = useState(
    'Notification' in window ? Notification.permission : 'unsupported'
  );
  const dropdownRef = useRef(null);
  const prevCountRef = useRef(0);

  // Check notification permission status
  const checkNotificationPermission = () => {
    if ('Notification' in window) {
      setNotificationPermission(Notification.permission);
    }
  };

  // Request browser notification permission
  const requestNotificationPermission = async () => {
    if ('Notification' in window) {
      const permission = await Notification.requestPermission();
      setNotificationPermission(permission);
      return permission;
    }
    return 'unsupported';
  };

  // Auto-request permission on mount (only if default, not if denied)
  useEffect(() => {
    checkNotificationPermission();
    if ('Notification' in window && Notification.permission === 'default') {
      // Don't auto-request - let user decide via the UI banner
      // requestNotificationPermission();
    }
  }, []);

  useEffect(() => {
    fetchUnreadCount();
    const interval = setInterval(fetchUnreadCount, 30000); // Poll every 30 seconds
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const fetchUnreadCount = async () => {
    try {
      const count = await notificationService.getUnreadCount();
      // Fire browser notification if count increased
      if (count > prevCountRef.current && prevCountRef.current >= 0) {
        try {
          const data = await notificationService.getNotifications({ limit: 1, unreadOnly: true });
          const newest = data.notifications?.[0];
          // Show browser notification for ALL notification types (if permission granted)
          if (newest && 'Notification' in window && Notification.permission === 'granted') {
            const notification = new Notification(newest.title || 'New Notification', {
              body: newest.message || 'You have a new notification',
              icon: '/favicon.ico',
              tag: newest._id,
              requireInteraction: false,
              silent: false
            });

            // Handle click on browser notification
            notification.onclick = () => {
              window.focus();
              if (newest.link) {
                navigate(newest.link);
              }
              notification.close();
            };
          }
        } catch (e) {
          console.error('Browser notification error:', e);
        }
      }
      prevCountRef.current = count;
      setUnreadCount(count);
    } catch (error) {
      console.error('Error fetching unread count:', error);
    }
  };

  const fetchNotifications = async () => {
    try {
      setLoading(true);
      const data = await notificationService.getNotifications({ limit: 10 });
      setNotifications(data.notifications);
      setUnreadCount(data.unreadCount);
    } catch (error) {
      console.error('Error fetching notifications:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleBellClick = () => {
    if (!isOpen) {
      fetchNotifications();
    }
    setIsOpen(!isOpen);
  };

  const handleMarkAsRead = async (notificationId) => {
    try {
      await notificationService.markOneAsRead(notificationId);
      setNotifications(prev =>
        prev.map(n => n._id === notificationId ? { ...n, isRead: true } : n)
      );
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (error) {
      console.error('Error marking as read:', error);
    }
  };

  const handleMarkAllAsRead = async () => {
    try {
      await notificationService.markAsRead();
      setNotifications(prev => prev.map(n => ({ ...n, isRead: true })));
      setUnreadCount(0);
    } catch (error) {
      console.error('Error marking all as read:', error);
    }
  };

  const handleDismiss = async (notificationId) => {
    try {
      await notificationService.dismissOne(notificationId);
      setNotifications(prev => prev.filter(n => n._id !== notificationId));
    } catch (error) {
      console.error('Error dismissing notification:', error);
    }
  };

  const handleNotificationClick = (notification) => {
    if (!notification.isRead) {
      handleMarkAsRead(notification._id);
    }
    if (notification.link) {
      navigate(notification.link);
      setIsOpen(false);
    }
  };

  const getTypeIcon = (type) => {
    const typeInfo = notificationService.getTypeInfo(type);
    return typeInfo.icon;
  };

  const getTypeColor = (type) => {
    const typeInfo = notificationService.getTypeInfo(type);
    const colors = {
      blue: 'text-blue-500',
      green: 'text-green-500',
      red: 'text-red-500',
      yellow: 'text-yellow-500',
      orange: 'text-orange-500',
      purple: 'text-purple-500',
      pink: 'text-pink-500',
      teal: 'text-teal-500',
      gray: 'text-gray-500'
    };
    return colors[typeInfo.color] || 'text-gray-500';
  };

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={handleBellClick}
        className={`relative p-2 rounded-full transition-colors ${
          darkMode
            ? 'hover:bg-gray-700 text-gray-300'
            : 'hover:bg-gray-200 text-gray-600'
        }`}
      >
        <FontAwesomeIcon icon={faBell} className="text-xl" />
        {unreadCount > 0 && (
          <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs font-bold rounded-full h-5 w-5 flex items-center justify-center">
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        )}
      </button>

      {isOpen && (
        <div
          className={`absolute right-0 mt-2 w-96 max-h-[500px] overflow-y-auto rounded-lg shadow-lg z-50 ${
            darkMode ? 'bg-gray-800 border border-gray-700' : 'bg-white border border-gray-200'
          }`}
        >
          {/* Header */}
          <div className={`sticky top-0 p-3 border-b ${
            darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'
          }`}>
            <div className="flex items-center justify-between">
              <h3 className="font-semibold">Notifications</h3>
              {unreadCount > 0 && (
                <button
                  onClick={handleMarkAllAsRead}
                  className="text-xs text-blue-500 hover:text-blue-600"
                >
                  <FontAwesomeIcon icon={faCheckDouble} className="mr-1" />
                  Mark all read
                </button>
              )}
            </div>
          </div>

          {/* Browser Notification Permission Banner */}
          {notificationPermission !== 'granted' && notificationPermission !== 'unsupported' && (
            <div className={`p-3 border-b ${
              darkMode ? 'bg-yellow-900/20 border-yellow-700' : 'bg-yellow-50 border-yellow-200'
            }`}>
              <div className="flex items-start space-x-2">
                <FontAwesomeIcon icon={faBellSlash} className="text-yellow-500 mt-0.5" />
                <div className="flex-1">
                  <p className={`text-sm font-medium ${darkMode ? 'text-yellow-200' : 'text-yellow-800'}`}>
                    {notificationPermission === 'denied'
                      ? 'Browser notifications blocked'
                      : 'Enable browser notifications?'}
                  </p>
                  <p className={`text-xs mt-1 ${darkMode ? 'text-yellow-300' : 'text-yellow-700'}`}>
                    {notificationPermission === 'denied'
                      ? 'Please enable notifications in your browser settings'
                      : 'Get notified even when this tab is not active'}
                  </p>
                  {notificationPermission === 'default' && (
                    <button
                      onClick={requestNotificationPermission}
                      className="mt-2 text-xs px-3 py-1 bg-yellow-500 hover:bg-yellow-600 text-white rounded"
                    >
                      Enable Notifications
                    </button>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Content */}
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <FontAwesomeIcon icon={faSpinner} spin className="text-2xl text-blue-500" />
            </div>
          ) : notifications.length === 0 ? (
            <div className={`py-8 text-center ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
              No notifications
            </div>
          ) : (
            <div>
              {notifications.map((notification) => (
                <div
                  key={notification._id}
                  className={`p-3 border-b cursor-pointer transition-colors ${
                    darkMode
                      ? `border-gray-700 ${notification.isRead ? 'bg-gray-800' : 'bg-gray-750'} hover:bg-gray-700`
                      : `border-gray-100 ${notification.isRead ? 'bg-white' : 'bg-blue-50'} hover:bg-gray-50`
                  }`}
                >
                  <div className="flex items-start">
                    <div className={`mt-1 mr-3 ${getTypeColor(notification.type)}`}>
                      <FontAwesomeIcon icon={faBell} />
                    </div>
                    <div
                      className="flex-1 min-w-0"
                      onClick={() => handleNotificationClick(notification)}
                    >
                      <p className={`font-medium text-sm ${!notification.isRead ? 'font-semibold' : ''}`}>
                        {notification.title}
                      </p>
                      <p className={`text-sm truncate ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                        {notification.message}
                      </p>
                      <p className={`text-xs mt-1 ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                        {notificationService.formatTime(notification.createdAt)}
                        {notification.actorName && ` • ${notification.actorName}`}
                      </p>
                    </div>
                    <div className="flex items-center space-x-1 ml-2">
                      {!notification.isRead && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleMarkAsRead(notification._id);
                          }}
                          className={`p-1 rounded hover:bg-gray-200 dark:hover:bg-gray-600`}
                          title="Mark as read"
                        >
                          <FontAwesomeIcon icon={faCheck} className="text-xs text-green-500" />
                        </button>
                      )}
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDismiss(notification._id);
                        }}
                        className={`p-1 rounded hover:bg-gray-200 dark:hover:bg-gray-600`}
                        title="Dismiss"
                      >
                        <FontAwesomeIcon icon={faTimes} className="text-xs text-gray-400" />
                      </button>
                      {notification.link && (
                        <FontAwesomeIcon
                          icon={faExternalLinkAlt}
                          className="text-xs text-gray-400"
                        />
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Footer */}
          <div className={`sticky bottom-0 p-2 border-t text-center ${
            darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'
          }`}>
            <button
              onClick={() => {
                navigate('/settings/notifications');
                setIsOpen(false);
              }}
              className="text-sm text-blue-500 hover:text-blue-600"
            >
              View all notifications
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default NotificationBell;
